import { Transform, Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsEnum,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  IsUUID,
  Min,
  ValidateNested,
} from 'class-validator';
import { MEASUREMENT_UNITS, type MeasurementUnit } from '../../label/measurement-unit';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';
import { DietaryTag } from './dietary-tag.enum';

/**
 * Normalizes free-text casing (AI extraction and older integrations send
 * lowercase, e.g. `"g"`) before `@IsIn(MEASUREMENT_UNITS)` runs, so the
 * accepted input surface does not shrink just because validation got strict.
 */
function normalizeMeasurementUnit({ value }: { value: unknown }): unknown {
  return typeof value === 'string' ? value.toUpperCase() : value;
}

export class IngredientDto {
  /**
   * Set only when the row already exists in the recipe: it is how the
   * repository's sync recognizes an edited row instead of deleting and
   * recreating it, which is what used to silently drop `weightG` on every save.
   */
  @IsOptional()
  // IDs already persisted by PostgreSQL are opaque UUID values. Some legacy
  // and demo rows use a valid UUID representation without an RFC version/
  // variant nibble, so strict validator.js UUID validation would make an
  // otherwise valid recipe impossible to edit in the frontend resolver.
  @IsUUID('loose')
  id?: string;

  @IsString()
  @IsNotEmpty()
  productName!: string;

  @IsNumber()
  @Min(0)
  quantity!: number;

  @Transform(normalizeMeasurementUnit)
  @IsIn(MEASUREMENT_UNITS)
  unit!: MeasurementUnit;

  /**
   * Second weight, always in grams. Omit it on a PATCH to leave the
   * previously stored value untouched; send it explicitly to set, or send
   * `null` to clear it explicitly — `undefined` (key omitted) and `null` are
   * NOT the same thing, see `CreateIngredientData`'s docblock in
   * `backend/src/recipe/repository/recipe.repository.interface.ts`. When
   * `unit` is `'G'` the server derives it from `quantity` regardless of what
   * is sent here.
   */
  @IsOptional()
  @IsNumber()
  @IsPositive()
  weightG?: number | null;

  /**
   * Id dell'`Ingredient` d'anagrafica collegato alla riga (denominazione
   * legale, allergeni per sostanza, densità). `undefined` lascia il
   * collegamento persistito intoccato; `null` lo stacca esplicitamente —
   * staccare una riga dall'anagrafica è un'azione legittima, non un default
   * da vietare.
   */
  @IsOptional()
  // Same persisted-ID boundary as `id` above: validate the PostgreSQL UUID
  // representation, not the UUID generation version used by old data.
  @IsUUID('loose')
  ingredientId?: string | null;

  /** Entra in ricetta già congelata da questa cucina. */
  @IsOptional()
  @IsBoolean()
  fromFrozen?: boolean;

  /** Arriva già congelata dal fornitore. */
  @IsOptional()
  @IsBoolean()
  frozenAtOrigin?: boolean;

  /** Art. 18 §3: this recipe row is an engineered nanomaterial. */
  @IsOptional()
  @IsBoolean()
  isEngineeredNanomaterial?: boolean;
}

export class SubRecipeRefDto {
  // A referenced recipe may also predate RFC-versioned UUID generation.
  @IsUUID('loose')
  subRecipeId!: string;

  @IsNumber()
  @Min(0)
  quantity!: number;

  @Transform(normalizeMeasurementUnit)
  @IsIn(MEASUREMENT_UNITS)
  unit!: MeasurementUnit;
}

export class CreateRecipeDto {
  @IsString()
  @IsNotEmpty()
  name!: string;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  description?: string;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  notes?: string;

  @IsOptional()
  @IsInt()
  @Min(1)
  servings?: number;

  /**
   * Categoria di organizzazione nella sezione ricette (anagrafica
   * `RecipeCategory` del tenant). Il backend rifiuta l'id di un altro tenant;
   * l'assenza lascia la ricetta "senza categoria".
   */
  @IsOptional()
  // Same persisted-ID boundary as IngredientDto.ingredientId: validate the
  // PostgreSQL UUID representation, not the generation version.
  @IsUUID('loose')
  categoryId?: string;

  @IsOptional()
  @IsInt()
  @Min(1)
  shelfLifeDays?: number;

  /**
   * Peso totale dopo cottura/lavorazione. Se assente, il calcolo nutrizionale
   * normalizza esplicitamente sul peso crudo degli ingredienti.
   */
  @IsOptional()
  @IsNumber()
  @IsPositive()
  finishedWeightG?: number;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientDto)
  ingredients?: IngredientDto[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SubRecipeRefDto)
  subRecipes?: SubRecipeRefDto[];

  @IsOptional()
  @IsArray()
  @IsEnum(DietaryTag, { each: true })
  dietaryTags?: DietaryTag[];
}
